import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Session, TranscriptDiscrepancy, TranscriptSegment } from '@/domain/schema/v1';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { fmtMs, RoleBadge, RolePicker } from '../../components/TranscriptViewer';
import { useTranscripts } from './TranscriptSection';

const CATEGORY: Record<string, string> = {
  risk: '위험 관련',
  negation: '부정 표현',
  number: '숫자·횟수',
  person: '인물·관계',
  medication: '약물',
  diagnosis: '진단',
  event: '사건',
  terminology: '용어',
  wording: '표현',
  unknown: '기타',
};

/** 전사 검증 (UX_FLOW §10): 중요한 곳만, 카드 단위로 */
export function VerifyScreen() {
  const { sessionId = '' } = useParams();
  const svc = useService();
  const ts = useTranscripts();
  const navigate = useNavigate();
  const { data, reload } = useLoad(async () => {
    const session = await svc.getSession(sessionId);
    return { session, items: await ts.reviewItems(session) };
  }, [svc, ts, sessionId]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const { session, items } = data;

  return (
    <section className="stack">
      <div className="page-head">
        <div>
          <h1>{items.length ? `${items.length}곳만 확인하면 됩니다` : '확인할 곳이 없습니다'}</h1>
          <p className="muted">
            위험·부정·숫자·인물처럼 의미가 달라질 수 있는 부분만 모았습니다. 전체 전사를 교정할 필요는 없습니다.
          </p>
        </div>
        <Link to={`/sessions/${sessionId}`}>회기로 돌아가기</Link>
      </div>
      {items.map(({ segment, discrepancy }) => (
        <VerifyCard key={segment.segmentId} session={session} segment={segment} d={discrepancy} onDone={reload} />
      ))}
      <button
        type="button"
        className="primary"
        onClick={() => void svc.completeVerification(sessionId).then(() => navigate(`/sessions/${sessionId}/ai`))}
      >
        {items.length ? '나머지는 나중에 — 분석으로 진행' : '검증 완료'}
      </button>
      {items.length > 0 && (
        <p className="muted small">확인하지 않은 줄은 "확인 필요"로 남고, 내담자 진술의 강한 근거로 쓰이지 않습니다.</p>
      )}
    </section>
  );
}

export function VerifyCard({
  session,
  segment,
  d,
  onDone,
}: {
  session: Session;
  segment: TranscriptSegment;
  d?: TranscriptDiscrepancy;
  onDone: () => void;
}) {
  const ts = useTranscripts();
  const [manual, setManual] = useState<string | null>(null);
  const act = (choice: 'samsung' | 'apple' | 'manual' | 'ignored', text?: string) =>
    void ts.resolveReview(session, segment.segmentId, choice, text).then(onDone);
  return (
    <div className={`card ${segment.clinicalPriority === 'critical' ? 'warn-card' : ''}`}>
      <div className="row small">
        <strong>{fmtMs(segment.startMs)}</strong>
        <RoleBadge seg={segment} />
        <span className={`badge ${segment.clinicalPriority === 'critical' ? 'badge-review' : ''}`}>
          {CATEGORY[d?.category ?? 'unknown'] ?? '확인'}
        </span>
      </div>
      <div className="compare">
        <div>
          <span className="muted small">갤럭시 전사</span>
          <p>{d?.samsungText ?? segment.text}</p>
        </div>
        <div>
          <span className="muted small">아이폰 전사</span>
          <p>{d?.appleText ?? '(대응 없음)'}</p>
        </div>
      </div>
      <RolePicker seg={segment} onPick={(r) => void ts.setRole(session, segment.segmentId, r).then(onDone)} />
      {manual !== null ? (
        <div className="stack">
          <textarea aria-label="직접 수정" rows={2} value={manual} onChange={(e) => setManual(e.target.value)} />
          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={!manual.trim()}
              onClick={() => act('manual', manual.trim())}
            >
              저장
            </button>
            <button type="button" onClick={() => setManual(null)}>
              취소
            </button>
          </div>
        </div>
      ) : (
        <div className="row">
          <button type="button" onClick={() => act('samsung')}>
            갤럭시 쪽
          </button>
          {d?.appleText && (
            <button type="button" onClick={() => act('apple')}>
              아이폰 쪽
            </button>
          )}
          <button type="button" onClick={() => setManual(segment.text)}>
            직접 수정
          </button>
          <button type="button" onClick={() => act('ignored')}>
            중요하지 않음
          </button>
        </div>
      )}
      <p className="muted small">
        원음 확인이 필요하면 녹음 앱에서 {fmtMs(segment.iphoneTimeRange?.startMs ?? segment.startMs)} 부근을 들어
        보세요.
      </p>
    </div>
  );
}
